import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { ApiAdimplencia } from '../../../../../services/api';
import { formatarMensagemErroTecnica } from '../../../../../helpers/formatarErrosAPI';

/**
 * Valor mínimo permitido para lançamento avulso.
 *
 * @type {number}
 */
const VALOR_MINIMO_LANCAMENTO = 30;

/**
 * Valor máximo padrão permitido para lançamento avulso.
 *
 * @type {number}
 */
const VALOR_MAXIMO_PADRAO_LANCAMENTO = 10000;

/**
 * Mapa de exceções de valor máximo por credor.
 *
 * @type {Record<string, number>}
 */
const VALOR_MAXIMO_POR_CREDOR = {
  '6e39ee2c-55e6-4e3b-bab5-623e377dd56b': 25000,
};

/**
 * Retorna o valor máximo permitido para o credor informado.
 *
 * @param {string|null|undefined} credorId
 * @returns {number}
 */
function getValorMaximoLancamentoPorCredor(credorId) {
  if (!credorId) {
    return VALOR_MAXIMO_PADRAO_LANCAMENTO;
  }

  return VALOR_MAXIMO_POR_CREDOR[credorId] ?? VALOR_MAXIMO_PADRAO_LANCAMENTO;
}

/**
 * Faz o parse de um valor monetário/numérico para number.
 * Aceita:
 * - number
 * - string simples ("2500")
 * - string BR ("2.500,00")
 * - string com prefixo "R$"
 *
 * @param {number|string|null|undefined} value
 * @returns {number}
 */
function parseNumericValue(value) {
  if (value === null || value === undefined || value === '') {
    return NaN;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : NaN;
  }

  const normalizedValue = String(value)
      .trim()
      .replace(/\s/g, '')
      .replace(/^R\$\s?/, '')
      .replace(/\./g, '')
      .replace(',', '.');

  const parsedValue = Number(normalizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : NaN;
}

/**
 * Formata um valor monetário inteiro para exibição pt-BR.
 *
 * @param {number} value
 * @returns {string}
 */
function formatCurrencyInteger(value) {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Converte strings de data para Date sem sofrer shift de fuso quando a API envia "YYYY-MM-DD".
 *
 * - "YYYY-MM-DD" -> interpreta como data local (YYYY-MM-DDT00:00:00)
 * - "YYYY-MM-DDTHH:mm:ss..." -> usa apenas a parte da data e interpreta como local
 * - "DD/MM/YYYY" -> interpreta como local
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
function parseDateToLocal(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value !== 'string') return null;

  const s = value.trim();

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [dd, mm, yyyy] = s.split('/');
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m?.[1]) {
    const d = new Date(`${m[1]}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Seleciona o "melhor" boleto para exibição quando existirem múltiplos.
 * Regra: pago > vencido > cancelado > faturado > aguardando > cadastrado > outros.
 *
 * @param {object} item
 * @returns {{ boleto_recorrencia_id?: string, boleto_situacao?: string } | undefined}
 */
function getBestBoleto(item) {
  const boletos = item?.faturamento?.boletos;
  if (!Array.isArray(boletos) || boletos.length === 0) return undefined;

  /** @type {Record<string, number>} */
  const weights = {
    pago: 60,
    vencido: 50,
    cancelado: 40,
    faturado: 30,
    aguardando: 20,
    cadastrado: 10,
  };

  let best = undefined;
  let bestW = -1;

  for (const b of boletos) {
    const situacao = (b?.boleto_situacao ?? '')
        .toString()
        .toLowerCase()
        .trim();

    const w = weights[situacao] ?? 0;
    if (situacao && w > bestW) {
      bestW = w;
      best = b;
    }
  }

  if (!best) best = boletos[0];

  return best
      ? {
        boleto_recorrencia_id: best?.boleto_recorrencia_id,
        boleto_situacao: (best?.boleto_situacao ?? '').toString().toLowerCase().trim() || undefined,
      }
      : undefined;
}

/**
 * Resolve o vencimento para exibição priorizando o boleto.
 *
 * @param {object} item
 * @returns {string|Date|null|undefined}
 */
function resolveVencimento(item) {
  const firstBoleto = item?.faturamento?.boletos?.[0];
  return (
      firstBoleto?.boleto_vencimento ||
      firstBoleto?.vencimento ||
      item?.boleto_vencimento ||
      item?.boleto?.vencimento ||
      item?.boleto_recorrencia_vencimento ||
      item?.lancamento_vencimento ||
      null
  );
}

/**
 * Normaliza a lista para UI.
 *
 * @param {Array<object>} lista
 * @returns {Array<object>}
 */
function normalizeLancamentos(lista) {
  return (Array.isArray(lista) ? lista : []).map((item) => {
    const bestBoleto = getBestBoleto(item);

    const situacao_exibicao = bestBoleto?.boleto_situacao || item?.lancamento_situacao;

    const vencimentoRaw = resolveVencimento(item);
    const vencimentoDate = parseDateToLocal(vencimentoRaw);
    const vencimento_exibicao = vencimentoDate ? format(vencimentoDate, 'dd/MM/yyyy') : '';

    const lancamentoVencimentoDate = parseDateToLocal(item?.lancamento_vencimento);

    return {
      ...item,
      boleto_recorrencia_id: bestBoleto?.boleto_recorrencia_id,
      boleto_situacao: bestBoleto?.boleto_situacao,
      situacao_exibicao,
      vencimento_exibicao,
      vencimento_date: vencimentoDate,
      lancamento_vencimento_date: lancamentoVencimentoDate,
    };
  });
}

/**
 * Hook customizado para gerenciar o estado e ações da página de lançamentos avulsos.
 *
 * @param {object} itemInfo
 * @returns {object}
 */
export function useLancamentoAvulso(itemInfo) {
  const uploadRef = useRef(null);
  const [boletoLoadingId, setBoletoLoadingId] = useState(null);

  const [showDialog, setShowDialog] = useState({
    create: false,
    update: false,
    delete: false,
    confirm: false,
  });

  const [lancamento, setLancamento] = useState({
    descricao: '',
    valor: '',
    dataRef: '',
    vencimento: '',
    imagem: {},
  });

  const [listaLancamentos, setListaLancamentos] = useState([]);
  const [rowData, setRowData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (itemInfo?.contratante_id) {
      getLancamentos(itemInfo.contratante_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemInfo?.contratante_id]);

  /**
   * Busca os lançamentos do contratante.
   *
   * @param {string} contratanteId
   * @returns {Promise<void>}
   */
  const getLancamentos = async (contratanteId) => {
    try {
      const { data } = await ApiAdimplencia.get(`/lancamentoAvulso/contratante/${contratanteId}`);

      if (data?.sucesso) {
        setListaLancamentos(normalizeLancamentos(data.dados || []));
      } else {
        toast.error(data?.mensagem || 'Erro ao carregar lançamentos.');
        setListaLancamentos([]);
      }
    } catch (error) {
      const resposta = error?.response?.data;
      toast.error(resposta?.mensagem || 'Houve um erro ao carregar os lançamentos.');
      setListaLancamentos([]);
    }
  };

  /**
   * Valida a imagem enviada.
   *
   * @param {File|undefined} imagemArquivo
   * @param {Function} limparImagem
   * @returns {boolean}
   */
  const validateImage = (imagemArquivo, limparImagem) => {
    const isImage = imagemArquivo?.type?.startsWith('image/');
    const isValidType = ['image/png', 'image/jpeg', 'image/jpg', 'image/jpe'].includes(imagemArquivo?.type);

    if (!isImage || !isValidType) {
      toast.error('Insira uma imagem no formato jpeg ou png.');
      limparImagem();
      return false;
    }

    return true;
  };

  /**
   * Faz upload da imagem do lançamento.
   *
   * @param {object} e
   * @returns {void}
   */
  const uploadImagem = (e) => {
    const imagemArquivo = e.files?.[0];
    const limparImagem = e.options.clear;

    if (!imagemArquivo) return;

    if (e.files.length > 1) {
      toast.error('Insira apenas uma imagem');
      limparImagem();
      return;
    }

    if (!validateImage(imagemArquivo, limparImagem)) return;

    const leitor = new FileReader();
    leitor.readAsDataURL(imagemArquivo);
    leitor.onload = (evt) => {
      const elementoImagem = new Image();
      elementoImagem.src = evt.target.result;

      elementoImagem.onload = () => {
        if (elementoImagem.height > 310 || elementoImagem.width > 550) {
          toast.error(`Dimensões inválidas: ${elementoImagem.width}x${elementoImagem.height}`);
          limparImagem();
        } else {
          setLancamento((prev) => ({
            ...prev,
            imagem: new Blob([imagemArquivo], { type: imagemArquivo.type }),
          }));
        }
      };
    };
  };

  /**
   * Valida o formulário antes da confirmação.
   *
   * @param {React.FormEvent<HTMLFormElement>|Event} e
   * @returns {void}
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    const { vencimento, dataRef, valor } = lancamento;
    const valorNumerico = parseNumericValue(valor);
    const valorMaximoPermitido = getValorMaximoLancamentoPorCredor(itemInfo?.credor_id);

    if (!vencimento || !dataRef) {
      return toast.info('Insira datas válidas.');
    }

    if (!Number.isFinite(valorNumerico) || valorNumerico < VALOR_MINIMO_LANCAMENTO || valorNumerico > valorMaximoPermitido) {
      return toast.info(
          `Por favor "Valor do Lançamento" deve estar entre R$ ${formatCurrencyInteger(VALOR_MINIMO_LANCAMENTO)} e R$ ${formatCurrencyInteger(valorMaximoPermitido)}.`
      );
    }

    if (new Date(vencimento) < new Date().setHours(0, 0, 0, 0)) {
      return toast.error('A data de vencimento deve ser maior ou igual à data atual.');
    }

    const action = showDialog.update ? 'update' : 'add';
    setShowDialog((prev) => ({ ...prev, confirm: action }));
  };

  /**
   * Cria o FormData para envio à API.
   *
   * @param {object} payload
   * @param {string} payload.descricao
   * @param {number|string} payload.valor
   * @param {Date} payload.vencimento
   * @param {Date} payload.dataRef
   * @param {Blob|object|null|undefined} payload.imagem
   * @param {string|number|null|undefined} payload.lancamento_id
   * @returns {FormData}
   */
  const createFormData = ({ descricao, valor, vencimento, dataRef, imagem, lancamento_id }) => {
    const formData = new FormData();
    const splitDate = format(dataRef, 'MM/yyyy').split('/');

    formData.append('file', imagem && imagem.type ? imagem : null);

    formData.append(
        'lancamentoData',
        JSON.stringify({
          lancamento_id,
          lancamento_descricao: descricao,
          lancamento_valor: parseNumericValue(valor),
          lancamento_vencimento: format(vencimento, 'yyyy-MM-dd'),
          mes_referencia: splitDate[0],
          ano_referencia: splitDate[1],
          contratante_id: itemInfo.contratante_id,
        })
    );

    return formData;
  };

  /**
   * Converte um base64 de PDF em Blob.
   *
   * @param {string} data
   * @returns {Blob}
   */
  const base64toBlob = (data) => {
    const base64WithoutPrefix = data.replace(/^data:application\/pdf;base64,/, '');
    const bytes = atob(base64WithoutPrefix);
    const byteArray = new Uint8Array(bytes.length);

    for (let i = 0; i < bytes.length; i++) {
      byteArray[i] = bytes.charCodeAt(i);
    }

    return new Blob([byteArray], { type: 'application/pdf' });
  };

  /**
   * Abre o PDF do boleto em nova aba.
   *
   * @param {string} boleto_recorrencia_id
   * @param {Window|null} targetWindow
   * @returns {Promise<void>}
   */
  const abrirBoleto = async (boleto_recorrencia_id, targetWindow = null) => {
    if (!boleto_recorrencia_id) {
      toast.info('Boleto indisponível para este lançamento.');
      if (targetWindow && !targetWindow.closed) targetWindow.close();
      return;
    }

    if (boletoLoadingId === boleto_recorrencia_id) return;

    setBoletoLoadingId(boleto_recorrencia_id);

    try {
      if (targetWindow && !targetWindow.closed) {
        targetWindow.document.title = 'Gerando PDF do boleto...';
      }

      const { data } = await ApiAdimplencia.get(`/boleto/${boleto_recorrencia_id}/pdf`);

      const base64pdf = data?.dados?.base64_pdf;
      if (!base64pdf) {
        toast.error('Não foi possível gerar o PDF do boleto.');
        if (targetWindow && !targetWindow.closed) targetWindow.close();
        return;
      }

      const base64 = `data:application/pdf;base64,${base64pdf}`;
      const blob = base64toBlob(base64);
      const url = URL.createObjectURL(blob);

      if (targetWindow && !targetWindow.closed) {
        targetWindow.location.href = url;
      } else {
        window.open(url);
      }

      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error('Erro ao abrir o boleto.');
      if (targetWindow && !targetWindow.closed) targetWindow.close();
    } finally {
      setBoletoLoadingId(null);
    }
  };

  /**
   * Executa a ação de adicionar, atualizar ou excluir lançamento.
   *
   * @param {'add'|'update'|'delete'} action
   * @returns {Promise<void>}
   */
  const handleAction = async (action) => {
    setIsLoading(true);

    try {
      if (['update', 'delete'].includes(action) && rowData.lancamento_situacao !== 'cadastrado') {
        toast.error('Apenas lançamentos com situação "Cadastrado" podem ser alterados ou excluídos.');
        return;
      }

      let response;

      if (action === 'add') {
        response = await ApiAdimplencia.post('/lancamentoAvulso', createFormData(lancamento));

        if (response.data?.dados?.boleto_recorrencia_id) {
          toast.success('Lançamento adicionado com sucesso!');
          await abrirBoleto(response.data?.dados?.boleto_recorrencia_id);
        }
      }

      if (action === 'update') {
        response = await ApiAdimplencia.put(
            `/lancamentoAvulso/${rowData.lancamento_id}`,
            createFormData({ ...lancamento, lancamento_id: rowData.lancamento_id })
        );
        toast.success('Lançamento atualizado com sucesso!');
      }

      if (action === 'delete') {
        response = await ApiAdimplencia.delete(`/lancamentoAvulso/${rowData.lancamento_id}`, {
          data: { lancamento_id: rowData.lancamento_id },
        });
        toast.success('Lançamento deletado com sucesso!');
      }

      if (response) {
        await getLancamentos(itemInfo.contratante_id);
        fecharDialogs();
      }
    } catch (error) {
      const resposta = error?.response?.data;
      const mensagem = resposta?.mensagem || 'Erro ao processar o lançamento.';

      toast.error(mensagem);

      if (Array.isArray(resposta?.detalhes) && resposta.detalhes.length > 0) {
        resposta.detalhes.forEach((detalhe) => {
          const texto = String(detalhe ?? '');
          if (texto.trim() && texto.trim() !== mensagem.trim()) {
            toast.warn(detalleFormatado(texto));
          }
        });
      }

      if (Array.isArray(resposta?.erros)) {
        resposta.erros.forEach((erro) => {
          toast.error(formatarMensagemErroTecnica(erro));
        });
      }
    } finally {
      setIsLoading(false);
      setShowDialog((prev) => ({ ...prev, confirm: false, delete: false }));
    }
  };

  /**
   * Fecha e reseta os diálogos.
   *
   * @returns {void}
   */
  const fecharDialogs = () => {
    setLancamento({
      descricao: '',
      valor: '',
      dataRef: '',
      vencimento: '',
      imagem: {},
    });

    setShowDialog({
      create: false,
      update: false,
      delete: false,
      confirm: false,
    });
  };

  /**
   * Formata uma linha de detalhe para exibição legível no toast.
   *
   * @param {string} texto
   * @returns {string}
   */
  const detalleFormatado = (texto) => {
    if (!texto.includes(':')) return texto;

    const [, msg] = texto.split(':');
    return `${(msg ?? '').trim()}`;
  };

  return {
    lancamento,
    setLancamento,
    listaLancamentos,
    rowData,
    setRowData,
    showDialog,
    setShowDialog,
    isLoading,
    uploadRef,
    handleSubmit,
    handleAction,
    uploadImagem,
    fecharDialogs,
    getLancamentos,
    abrirBoleto,
    boletoLoadingId,
  };
}